import { type Area } from "react-easy-crop";

/**
 * Creates a cropped image from a source image and crop area.
 * Returns a Blob of the cropped image in JPEG format.
 */
export async function getCroppedImageFromArea(imageSource: string, cropArea: Area): Promise<Blob> {
  try {
    const image = await createImage(imageSource);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Failed to get canvas context");
    }

    // Set canvas size to crop size
    canvas.width = cropArea.width;
    canvas.height = cropArea.height;

    // Draw the cropped image
    context.drawImage(
      image,
      cropArea.x,
      cropArea.y,
      cropArea.width,
      cropArea.height,
      0,
      0,
      cropArea.width,
      cropArea.height
    );

    return await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      }, "image/jpeg");
    });
  } catch (error) {
    throw new Error(
      `Failed to crop image: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

/**
 * Helper function to create an Image element from a source URL
 */
async function createImage(url: string): Promise<HTMLImageElement> {
  return await new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => {
      resolve(image);
    });
    image.addEventListener("error", (error) => {
      reject(error);
    });
    image.src = url;
  });
}
